import { useState, useRef, useMemo, useCallback } from 'react';
import debounce from 'lodash.debounce';
import { OptionType } from 'commons/components/MultiSelect/multiSelectStyleConfig';
import {
  searchCouponsByQuery,
  getCoupon,
} from 'domains/Marketing/Coupons/couponSearchService';

function useOrderCouponSearchOptions() {
  const [options, setOptions] = useState<OptionType[]>([]);
  const [loading, setLoading] = useState(false);

  const cache = useRef<Map<string, OptionType[]>>(new Map());
  const allOptionsRef = useRef<OptionType[]>([]);

  type RawCoupon = { id: number | string; code: string };

  const formatCoupons = useCallback(
    (results: RawCoupon[]): OptionType[] =>
      results.map((coupon) => ({
        label: coupon.code,
        value: String(coupon.id),
      })),
    [],
  );

  const mergeOptions = useCallback((formattedResults: OptionType[]) => {
    const incomingValues = new Set(formattedResults.map((r) => r.value));
    const existingFiltered = allOptionsRef.current.filter(
      (opt) => !incomingValues.has(opt.value),
    );
    allOptionsRef.current = [...existingFiltered, ...formattedResults];
  }, []);

  const debouncedSearch = useMemo(
    () =>
      debounce(async (query: string) => {
        if (query.length < 2) {
          setLoading(false);
          return;
        }

        if (cache.current.has(query)) {
          setOptions(cache.current.get(query) ?? []);
          return;
        }

        setLoading(true);
        try {
          const results = await searchCouponsByQuery(query);
          const formattedResults = formatCoupons(results);

          cache.current.set(query, formattedResults);
          mergeOptions(formattedResults);

          setOptions(formattedResults);
        } catch {
          setOptions([]);
        } finally {
          setLoading(false);
        }
      }, 300),
    [formatCoupons, mergeOptions],
  );

  const onInputChange = (inputValue: string) => {
    debouncedSearch(inputValue);
  };

  const getDefaultCouponValues = useCallback(
    async (idsString?: string): Promise<OptionType[]> => {
      if (!idsString) return [];

      const ids = idsString.split(',').filter(Boolean);

      const cachedOptions = ids
        .map((id) => allOptionsRef.current.find((opt) => opt.value === id))
        .filter((opt): opt is OptionType => Boolean(opt));

      const cachedIds = new Set(cachedOptions.map((opt) => opt.value));
      const missingIds = ids.filter((id) => !cachedIds.has(id));

      let fetchedOptions: OptionType[] = [];

      if (missingIds.length > 0) {
        const coupons = await Promise.all(
          missingIds.map(async (id) => {
            try {
              const coupon = await getCoupon(id);
              return {
                label: coupon.code,
                value: String(coupon.id),
              };
            } catch {
              return {
                label: id,
                value: id,
              };
            }
          }),
        );

        fetchedOptions = coupons;
        allOptionsRef.current.push(
          ...fetchedOptions.filter(
            (opt) => !allOptionsRef.current.some((o) => o.value === opt.value),
          ),
        );
      }

      return [...cachedOptions, ...fetchedOptions];
    },
    [],
  );

  return {
    options,
    loading,
    onInputChange,
    getDefaultCouponValues,
  };
}

export default useOrderCouponSearchOptions;
